import React, { useState, useEffect, useRef, useCallback } from 'react';

const Service = ({ currentLanguage = 'EN' }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [categories, setCategories] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [imageSources, setImageSources] = useState({});
  const loadMoreRef = useRef(null);

  const defaultImage = "https://images.unsplash.com/photo-1556228570-3ce5b2ce9d2e?w=300&h=200&fit=crop&auto=format";

  useEffect(() => {
    fetch('/services.json')
      .then((res) => res.json())
      .then((data) => {
        let processedServices = [];
        let categorySet = new Set();
        
        Object.entries(data.services).forEach(([category, categoryServices]) => {
          const formattedCategory = category.replace(/_/g, ' ').toUpperCase();
          categorySet.add(formattedCategory);
          
          categoryServices.forEach((service, index) => {
            processedServices.push({
              id: `${category}-${index}`,
              title: service.name,
              description: service.description,
              detailedDescription: service.description,
              price: `$${service.price}`,
              duration: "30-60 mins",
              image: service.image || defaultImage,
              category: formattedCategory,
              features: ["Professional application", "Quality materials", "Customized design"],
              benefits: ["Long-lasting results", "Safe and hygienic", "Relaxing experience", "Personalized care"]
            });
          });
        });
        
        setAllServices(processedServices);
        setCategories(['ALL', ...Array.from(categorySet)]);
        setImageSources(processedServices.reduce((acc, s) => {
          acc[s.id] = s.image;
          return acc;
        }, {}));
        setIsFetching(false);
      })
      .catch((error) => {
        console.error('Error fetching services:', error);
        setIsFetching(false);
      });
  }, []);

  // Reset visible count when category changes
  useEffect(() => {
    setVisibleCount(10);
  }, [selectedCategory]);

  const loadMoreServices = useCallback(() => {
    const currentServices = selectedCategory === 'ALL' 
      ? allServices 
      : allServices.filter(service => service.category === selectedCategory);
    
    if (visibleCount >= currentServices.length) {
      return; // No more services to load
    }

    setIsLoading(true);
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      setVisibleCount(prevCount => prevCount + 10);
      setIsLoading(false);
    }, 500);
  }, [visibleCount, allServices, selectedCategory]);

  // Fixed Intersection Observer for auto-loading
  useEffect(() => {
    // Capture the current ref value
    const currentRef = loadMoreRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          loadMoreServices();
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [visibleCount, isLoading, allServices, selectedCategory, loadMoreServices]); // Added loadMoreServices to dependencies

  const handleImageLoad = (serviceId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [serviceId]: true
    }));
  };

  const handleImageError = (serviceId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [serviceId]: true // Mark as loaded even if error to hide loading state
    }));
    setImageSources(prev => ({
      ...prev,
      [serviceId]: defaultImage
    }));
  };

  const socialLinks = {
    instagram: "https://www.instagram.com/kimhuotlim?igsh=cmhxYmswcjF6Nm82&utm_source=qr",
    facebook: "https://web.facebook.com/people/The-Gilded-OWL-Nail-salon-SPA/61580336022975/?_rdc=1&_rdr#"
  };

  const currentServices = selectedCategory === 'ALL' 
    ? allServices 
    : allServices.filter(service => service.category === selectedCategory);

  const visibleServices = currentServices.slice(0, visibleCount);
  const hasMoreServices = visibleCount < currentServices.length;

  const handleBookNow = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleSocialClick = (platform) => {
    window.open(socialLinks[platform], '_blank');
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Manual load more button handler
  const handleLoadMoreClick = () => {
    loadMoreServices();
  };

  if (isFetching) {
    return (
      <section className="py-8 bg-gray-50 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading services...</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{`
        .font-inter {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .category-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f7fafc;
        }

        .category-scroll::-webkit-scrollbar {
          height: 6px;
        }

        .category-scroll::-webkit-scrollbar-track {
          background: #f7fafc;
          border-radius: 3px;
        }

        .category-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }

        .category-scroll::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        .loading-spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #B08B71;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .image-loading {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .service-image {
          transition: opacity 0.3s ease;
        }

        .service-image.loading {
          opacity: 0;
        }

        .service-image.loaded {
          opacity: 1;
        }
      `}</style>

      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          {/* Your header content here */}

          {/* Category Filter Buttons */}
          <div className="mb-8">
            <div className="category-scroll flex gap-2 sm:gap-3 pb-2 sm:flex-wrap sm:justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-semibold text-sm sm:text-base whitespace-nowrap transition-all font-inter ${
                    selectedCategory === category
                      ? 'bg-[#B08B71] text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {visibleServices.map((service) => (
              <div 
                key={service.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Service Image with Loading State */}
                <div className="relative h-48 sm:h-40 md:h-48 overflow-hidden">
                  {!imageLoadStates[service.id] && (
                    <div className="absolute inset-0 image-loading"></div>
                  )}
                  <img 
                    src={imageSources[service.id] || service.image} 
                    alt={service.title}
                    className={`w-full h-full object-cover service-image ${
                      imageLoadStates[service.id] ? 'loaded' : 'loading'
                    }`}
                    onLoad={() => handleImageLoad(service.id)}
                    onError={() => handleImageError(service.id)}
                  />
                  <div className="absolute top-2 left-2 bg-gray-800 text-white px-2 py-1 rounded text-xs font-semibold">
                    {service.category}
                  </div>
                  <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-[#B08B71] text-white px-2 py-1 rounded-lg text-xs md:text-sm font-semibold">
                    {service.price}
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-3 sm:p-4 md:p-5">
                  <h3 className={`text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-2 line-clamp-2 font-inter`}>
                    {service.title}
                  </h3>
                  
                  <p className={`hidden md:block text-gray-600 text-sm mb-3 font-inter`}>
                    {service.description}
                  </p>
                  
                  <button 
                    onClick={() => handleBookNow(service)}
                    className={`w-full bg-[#B08B71] hover:bg-[#9c7a62] text-white py-2 md:py-2.5 rounded-lg text-xs sm:text-sm md:text-base font-medium font-inter transition-colors`}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* No Results Message */}
          {currentServices.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg font-inter">
                No services found in this category.
              </p>
            </div>
          )}

          {/* Load More Section */}
          {hasMoreServices && (
            <div className="mt-12 text-center">
              {/* Auto-load trigger element */}
              <div ref={loadMoreRef} className="h-10"></div>
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="mb-6">
                  <div className="loading-spinner"></div>
                  <p className="text-gray-600 mt-2 font-inter">
                    Loading more services...
                  </p>
                </div>
              )}

              {/* Manual load more button */}
              {!isLoading && (
                <button
                  onClick={handleLoadMoreClick}
                  className="bg-[#B08B71] hover:bg-[#9c7a62] text-white px-8 py-3 rounded-lg font-semibold font-inter transition-colors"
                >
                  Load More Services ({currentServices.length - visibleCount} remaining)
                </button>
              )}
            </div>
          )}

          {/* End of results message */}
          {!hasMoreServices && currentServices.length > 0 && (
            <div className="text-center mt-12">
              <p className="text-gray-500 font-inter">
                You've seen all {currentServices.length} services in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Service Detail Modal */}
      {isModalOpen && selectedService && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-opacity-50" 
            onClick={closeModal}
          ></div>
          
          <div className="flex items-center justify-center min-h-full p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-50 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[90vh] p-6">
                {/* Header with Image */}
                <div className="relative rounded-lg overflow-hidden mb-6">
                  <img 
                    src={imageSources[selectedService.id] || selectedService.image} 
                    alt={selectedService.title}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(selectedService.id)}
                  />
                  <div className="absolute inset-0 bg-opacity-30"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h2 className={`text-2xl md:text-3xl font-bold mb-2 font-inter`}>
                      {selectedService.title}
                    </h2>
                    <div className="flex items-center space-x-4">
                      <span className="bg-[#B08B71] px-3 py-1 rounded-lg text-sm font-semibold">
                        {selectedService.price}
                      </span>
                      <span className="flex items-center text-sm">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {selectedService.duration}
                      </span>
                    </div>
                    <span className="inline-block bg-gray-800 text-white px-2 py-1 rounded text-sm mt-2">
                      {selectedService.category}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div>
                    <h3 className={`text-xl font-semibold text-gray-900 mb-3 font-inter`}>
                      Service Description
                    </h3>
                    <p className={`text-gray-600 mb-6 font-inter`}>
                      {selectedService.detailedDescription}
                    </p>

                    <h4 className={`text-lg font-semibold text-gray-900 mb-3 font-inter`}>
                      What's Included:
                    </h4>
                    <div className="space-y-2 mb-6">
                      {selectedService.features.map((feature, index) => (
                        <div key={index} className="flex items-center">
                          <svg className="w-5 h-5 text-[#B08B71] mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className={`text-gray-700 font-inter`}>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div>
                    <h4 className={`text-lg font-semibold text-gray-900 mb-3 font-inter`}>
                      Benefits:
                    </h4>
                    <div className="space-y-2 mb-6">
                      {selectedService.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center">
                          <svg className="w-5 h-5 text-[#B08B71] mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className={`text-gray-700 font-inter`}>{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* Contact Buttons */}
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                      <h4 className={`text-lg font-semibold text-gray-900 mb-3 text-center font-inter`}>
                        Book This Service
                      </h4>
                      <p className={`text-sm text-gray-600 mb-4 text-center font-inter`}>
                        Contact us to make an appointment
                      </p>
                      
                      <div className="space-y-3">
                        {/* Instagram Button */}
                        <button 
                          onClick={() => handleSocialClick('instagram')}
                          className="w-full bg-pink-500 hover:bg-pink-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          <span className="font-inter">
                            Book via Instagram
                          </span>
                        </button>

                        {/* Facebook Button */}
                        <button 
                          onClick={() => handleSocialClick('facebook')}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          <span className="font-inter">
                            Book via Facebook
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Service;